import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { contactFormData } from '../mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    revenue: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success('Thank you! We\'ll contact you within 24 hours with your free AI audit.', {
        duration: 5000,
      });
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        revenue: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-cyan-400 text-slate-900 hover:bg-cyan-500 px-4 py-1.5 text-sm font-semibold">
            GET STARTED
          </Badge>
          <h2 className="text-5xl font-bold text-white mb-6">
            {contactFormData.title}
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {contactFormData.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="fullName" className="text-slate-900 font-semibold mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      placeholder="John Smith"
                      className="border-2 border-slate-300 focus:border-blue-500 rounded-lg h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-900 font-semibold mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@company.com"
                      className="border-2 border-slate-300 focus:border-blue-500 rounded-lg h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-slate-900 font-semibold mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="(555) 123-4567"
                      className="border-2 border-slate-300 focus:border-blue-500 rounded-lg h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-slate-900 font-semibold mb-2 block">
                      Company Name *
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      placeholder="Your Company LLC"
                      className="border-2 border-slate-300 focus:border-blue-500 rounded-lg h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="industry" className="text-slate-900 font-semibold mb-2 block">
                      Industry *
                    </Label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => handleSelectChange('industry', value)}
                      required
                    >
                      <SelectTrigger className="border-2 border-slate-300 focus:border-blue-500 rounded-lg h-12">
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactFormData.fields.find(f => f.name === 'industry').options.map((option, idx) => (
                          <SelectItem key={idx} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="revenue" className="text-slate-900 font-semibold mb-2 block">
                      Annual Revenue
                    </Label>
                    <Select
                      value={formData.revenue}
                      onValueChange={(value) => handleSelectChange('revenue', value)}
                    >
                      <SelectTrigger className="border-2 border-slate-300 focus:border-blue-500 rounded-lg h-12">
                        <SelectValue placeholder="Select range (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactFormData.fields.find(f => f.name === 'revenue').options.map((option, idx) => (
                          <SelectItem key={idx} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-900 font-semibold mb-2 block">
                    What's your biggest challenge?
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your business challenges and goals..."
                    rows={5}
                    className="border-2 border-slate-300 focus:border-blue-500 rounded-lg resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {isSubmitting ? 'Sending...' : 'Get Your Free AI Audit'}
                  <Send className="ml-2 h-5 w-5" />
                </Button>

                <p className="text-sm text-slate-500 text-center">
                  By submitting this form, you agree to our privacy policy. We'll never share your information.
                </p>
              </form>
            </div>
          </div>

          {/* Contact Info & Trust Signals */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Get In Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-cyan-400 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-slate-900" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Email Us</div>
                    <a href="mailto:info@aiasap.io" className="text-cyan-400 hover:underline">
                      info@aiasap.io
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-cyan-400 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-slate-900" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Call Us</div>
                    <a href="tel:+13463100995" className="text-cyan-400 hover:underline">
                      (346) 310-0995
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-cyan-400 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-slate-900" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Headquarters</div>
                    <p className="text-slate-300">
                      Houston, TX 77077
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Signals */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-4">What Happens Next?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-cyan-400 rounded-full flex items-center justify-center font-bold text-slate-900 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Free AI Audit</div>
                    <p className="text-sm text-slate-300">
                      We analyze your current processes and identify opportunities
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-cyan-400 rounded-full flex items-center justify-center font-bold text-slate-900 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Custom Strategy</div>
                    <p className="text-sm text-slate-300">
                      Receive a tailored implementation plan with ROI projections
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-cyan-400 rounded-full flex items-center justify-center font-bold text-slate-900 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Fast Implementation</div>
                    <p className="text-sm text-slate-300">
                      Go live in 7-14 days with full support
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
